import { useSubcategories } from '~/entities/categories'
import { getIsCurrentStep, useCurrentDocument } from '~/entities/document'
import type { Flag } from '~/shared/api/data-ingestion-api/client'
import { useDocumentFlags } from '../api'
import { STEP_FLAG_TYPE_TO_REVIEWED_STEP, type StepFlagType } from '../config/flag-types'
import { getActiveFlagByType } from './get-active-flag'

interface StepFlagState {
  activeFlag: Flag | null
  isCurrentStep: boolean
  hasActiveStepFlag: boolean
}

/**
 * Convenience hook for step-level flag UI. Composes current-document data,
 * subcategories, and the document-flags query so each step page can render
 * a `<StepFlagButton />` and swap its primary CTA without wiring the same
 * pieces by hand.
 *
 * `stepFlagType` is one of the `STEP_*` values from `FLAG_TYPES`. The step
 * token to compare against `getDocumentStep` is looked up via
 * `STEP_FLAG_TYPE_TO_REVIEWED_STEP`.
 */
export function useStepFlagState(stepFlagType: StepFlagType): StepFlagState {
  const {
    data: { document },
  } = useCurrentDocument()
  const subcategories = useSubcategories()
  const { data: flagsData } = useDocumentFlags()

  const activeFlag = getActiveFlagByType(flagsData.items, stepFlagType)
  const stepToken = STEP_FLAG_TYPE_TO_REVIEWED_STEP[stepFlagType]
  const isCurrentStep = getIsCurrentStep(document, subcategories, stepToken)

  return {
    activeFlag,
    isCurrentStep,
    hasActiveStepFlag: activeFlag !== null,
  }
}
